import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { formatLabel, timeAgo, STATUS_STYLES, RISK_CHART_COLORS, ACTION_LABELS } from '@/lib/format';
import { Folder, ShieldCheck, Bell, Warning, MagnifyingGlass, UserCircle, ArrowUp, ArrowRight, FileText, Key, CloudArrowDown, Scales, CheckCircle, PencilSimple } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Joyride } from 'react-joyride';
import tourSteps from '@/lib/tourSteps';
import { HelpTooltip } from '@/components/HelpTooltip';

const ICON_MAP = { FileText, Key, MagnifyingGlass, ShieldCheck, Bell, CloudArrowDown, Scales, CheckCircle, PencilSimple, ArrowUp, Warning, Folder };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => { document.title = "Radar — Dashboard"; }, []);
  const [sources, setSources] = useState([]);
  const [runTour, setRunTour] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/data-sources').then(r => setSources(r.data.sources || [])).catch(() => {});
    // Feature 18: Auto-start tour for first-time users
    if (!localStorage.getItem('radar_tour_completed')) setRunTour(true);
  }, []);

  if (!stats) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>;

  // Data freshness for sanctions/PEP cards
  const sanctionsSource = sources.find(s => s.source_id === 'OFAC_SDN');
  const pepSource = sources.find(s => s.source_id === 'PEP_OPENSANCTIONS');
  const isStale = (s) => !s?.last_synced || (Date.now() - new Date(s.last_synced).getTime()) > 48 * 3600000;

  // 2A: Clickable metric cards
  const metricCards = [
    { label: 'Total Cases', value: stats.total_cases, sub: `${stats.pending_review} pending review`, icon: Folder, color: '#0052FF', onClick: () => navigate('/cases') },
    { label: 'Monitoring Hits', value: stats.pending_monitoring_hits, sub: `${stats.monitored_cases} monitored`, icon: Bell, color: '#FF3B30', onClick: () => navigate('/monitoring') },
    { label: 'Match Alerts', value: stats.pending_match_alerts || 0, sub: 'pending resolution', icon: Warning, color: '#FF9500', onClick: () => navigate('/cases?status=under_review') },
    { label: 'High Risk', value: stats.high_risk_cases, sub: `${stats.escalated_cases} escalated`, icon: ArrowUp, color: '#FFCC00', onClick: () => navigate('/cases?risk=High') },
  ];

  // 2D: DB cards with freshness
  const dbCards = [
    { label: 'Sanctions Entries', value: stats.sanctions_entries?.toLocaleString(), icon: ShieldCheck, color: '#0052FF', freshness: sanctionsSource, onClick: () => navigate('/data-sources') },
    { label: 'PEP Profiles', value: stats.pep_entries?.toLocaleString(), icon: UserCircle, color: '#34C759', freshness: pepSource, onClick: () => navigate('/data-sources') },
    { label: 'Screenings', value: stats.total_screenings, icon: MagnifyingGlass, color: '#52525B', onClick: () => navigate('/reports') },
  ];

  // 2B: Pending action cards
  const actionItems = [
    { count: stats.pending_match_alerts || 0, label: 'Match Alerts', sub: 'Pending Resolution', path: '/cases', icon: Warning },
    { count: stats.pending_monitoring_hits, label: 'Monitoring Hits', sub: 'Pending Resolution', path: '/monitoring', icon: Bell },
    { count: stats.pending_review, label: 'Cases Under Review', sub: 'Awaiting Decision', path: '/cases?status=under_review', icon: Folder },
  ];

  // 2C: Human-readable activity
  const getActionInfo = (action) => ACTION_LABELS[action] || { label: formatLabel(action), icon: 'FileText' };

  return (
    <div data-testid="dashboard-page">
      {/* Feature 18: Guided Tour */}
      <Joyride steps={tourSteps} run={runTour} continuous showSkipButton showProgress callback={(data) => { if (data.status === 'finished' || data.status === 'skipped') { setRunTour(false); localStorage.setItem('radar_tour_completed', 'true'); } }} styles={{ options: { primaryColor: '#0052FF', zIndex: 10000 }, tooltip: { borderRadius: 8 } }} />
      <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-1">Dashboard</h1>
      <p className="text-sm text-[#52525B] mb-6">Real-time overview of your compliance operations</p>

      {/* 2B: Action Items Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 action-cards">
        {actionItems.map(a => (
          <div key={a.label} onClick={() => navigate(a.path)} className={`rounded-md p-4 cursor-pointer transition-all hover:shadow-md ${a.count > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <a.icon size={24} weight="duotone" className={a.count > 0 ? 'text-amber-600' : 'text-green-600'} />
                <div>
                  <p className="text-2xl font-bold text-[#0A0A0A]">{a.count}</p>
                  <p className="text-sm font-medium text-[#0A0A0A]">{a.label}</p>
                  <p className="text-xs text-[#52525B]">{a.count > 0 ? a.sub : 'All Clear'}</p>
                </div>
              </div>
              {a.count > 0 && (
                <div className="flex items-center gap-1 text-xs font-medium text-amber-700">
                  Review <ArrowRight size={14} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 2A: Clickable Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metricCards.map(c => (
          <div key={c.label} onClick={c.onClick} className="bg-white border border-[#E4E4E7] rounded-md p-5 cursor-pointer hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-2">
              <c.icon size={22} weight="duotone" style={{ color: c.color }} />
              <span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">{c.label}</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{c.value}</p>
            {c.sub && <p className="text-xs text-[#A1A1AA] mt-1">{c.sub}</p>}
          </div>
        ))}
      </div>

      {/* 2D: DB Cards with Freshness */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {dbCards.map(c => (
          <div key={c.label} onClick={c.onClick} className="bg-white border border-[#E4E4E7] rounded-md p-5 cursor-pointer hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-2">
              <c.icon size={22} weight="duotone" style={{ color: c.color }} />
              <span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">{c.label}</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{c.value}</p>
            {c.freshness && (
              <p className={`text-xs mt-1 ${isStale(c.freshness) ? 'text-red-600' : 'text-[#A1A1AA]'}`}>
                {isStale(c.freshness) && '⚠️ '}Last synced: {timeAgo(c.freshness.last_synced)}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Cases by Status</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.cases_by_status?.map(s => ({ ...s, status: formatLabel(s.status) }))}>
              <XAxis dataKey="status" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#0052FF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Risk Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={stats.cases_by_risk} dataKey="count" nameKey="rating" cx="50%" cy="50%" outerRadius={90} label={({ rating, count }) => `${rating}: ${count}`}>
                {stats.cases_by_risk?.map((e, i) => <Cell key={i} fill={RISK_CHART_COLORS[e.rating] || '#ccc'} />)}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 2C: Human-Readable Recent Activity */}
      {stats.recent_activity?.length > 0 && (
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <h3 className="font-heading text-base font-semibold mb-3">Recent Activity</h3>
          <div className="space-y-1">
            {stats.recent_activity.slice(0, 8).map((a, i) => {
              const info = getActionInfo(a.action);
              const IconComp = ICON_MAP[info.icon] || FileText;
              return (
                <div key={i} onClick={() => a.entity_id && a.entity_type === 'case' ? navigate(`/cases/${a.entity_id}`) : null}
                  className={`flex items-center justify-between py-2 px-2 rounded-md border-b border-[#F0F0F1] last:border-0 ${a.entity_id ? 'cursor-pointer hover:bg-[#F7F7F8]' : ''} transition-all`}>
                  <div className="flex items-center gap-3">
                    <IconComp size={16} weight="duotone" className="text-[#52525B] shrink-0" />
                    <span className="text-sm text-[#0A0A0A]">{info.label}</span>
                    <span className="text-xs text-[#A1A1AA]">{a.user_email}</span>
                  </div>
                  <span className="text-xs text-[#A1A1AA] shrink-0">{timeAgo(a.timestamp)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
